
import {
  enviarFotoDeLead as uploadLeadPhoto,
  listarCampanhas,
  listarClientesCadastrados,
  excluirLeads,
  listarLeads,
  salvarCampanha,
  salvarLeads,
  syncToAll,
} from './dados-service';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface SupabaseErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: any;
}

function handleSupabaseError(error: any, operationType: OperationType, path: string | null) {
  const errInfo: SupabaseErrorInfo = {
    error: error?.message || String(error),
    operationType,
    path,
    authInfo: {
      userId: 'anonymous_or_managed_key'
    }
  };
  console.error('Supabase Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export interface Lead {
  place_id: string;
  name: string;
  formatted_address?: string;
  formatted_phone_number?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  vicinity?: string;
  scannedAt: number;
  isEnriched?: boolean;
  enriched_at?: number;
  whatsapp_status?: 'pending' | 'exists' | 'not_exists';
  downloaded_at?: number;
  campaignId?: string;
  stepId?: string;
  types?: string[];
  geometry?: any;
  photos?: any[];
  whatsappData?: {
    picture?: string;
    status?: string;
    pushName?: string;
    isBusiness?: boolean;
    description?: string;
    businessWebsite?: string;
    category?: string;
    lastUpdated?: string;
    verified?: boolean;
    exists?: boolean;
    error?: string;
  };
}

class LeadService {
  private leads: Lead[] = [];

  constructor() {
    this.loadFromStorage();
  }

  async migrate() {
    const saved = localStorage.getItem('google_maps_leads_master');
    if (!saved) {
      console.log('LeadService: No local leads to migrate.');
      await this.refresh();
      return;
    }

    try {
      const localLeads: Lead[] = JSON.parse(saved);
      if (localLeads.length > 0) {
        console.log(`LeadService: Found ${localLeads.length} leads in localStorage. Migrating to Supabase...`);
        // Use the existing saveLeads logic which handles sanitization and cloud sync
        const count = await this.saveLeads(localLeads);
        console.log(`LeadService: Migration attempted for ${localLeads.length} leads. Successfully processed ${count}.`);
      }
      // ONLY remove if we successfully called saveLeads and it didn't throw
      localStorage.removeItem('google_maps_leads_master');
    } catch (e) {
      console.error('LeadService: Migration failed', e);
    } finally {
      await this.refresh();
    }
  }

  private async initialSync() {
    await this.refresh();
  }

  /**
   * Força a sincronização com o cloud e notifica ouvintes.
   * Modificado para ser mais leve, carregando apenas os últimos 500 leads por padrão.
   */
  async refresh() {
    try {
      // Antes: `select(15 colunas).order().limit(500)` direto no banco, a partir
      // do navegador, com a chave anônima. Agora é a API, com paginação por
      // cursor — e o teto de 500 é do servidor, não uma gentileza do cliente.
      const remoteLeads = await listarLeads(500);

      // Mescla por place_id, preservando o que está em memória e não voltou
      // nesta página. O refresh antigo substituía a lista inteira pelos 500 mais
      // recentes — e um lead capturado agora, fora dessa janela, sumia da tela.
      const porId = new Map(this.leads.map((l: any) => [l.place_id, l]));
      for (const remoto of remoteLeads) {
        porId.set(remoto.place_id, { ...porId.get(remoto.place_id), ...remoto });
      }
      this.leads = [...porId.values()] as any;
      window.dispatchEvent(new CustomEvent('leads-persisted'));
    } catch (e) {
      console.error('LeadService: falha ao sincronizar leads', e);
    }
  }

  /**
   * Obtém a contagem total de leads de uma campanha
   */
  async getCampaignLeadCount(campaignId: string): Promise<number> {
    try {
      // O `select('*', { count: 'exact', head: true })` de antes fazia um COUNT
      // exato na tabela inteira a cada chamada. A campanha já carrega
      // `totalLeads`, mantido pelo servidor quando os leads são gravados.
      const campanhas = await listarCampanhas();
      return campanhas.find((c) => c.id === campaignId)?.totalLeads ?? 0;
    } catch (e) {
      console.error('LeadService: Failed to get campaign count', e);
      return 0;
    }
  }

  /**
   * Busca um bloco específico de leads de uma campanha.
   * Modificado para substituir os leads em memória (focando no bloco atual).
   */
  async fetchLeadsRange(campaignId: string, from: number, to: number): Promise<Lead[]> {
    try {
      // `range(from, to)` virava OFFSET no Postgres: cada bloco relia e
      // descartava tudo que veio antes. A paginação por cursor de `listarLeads`
      // tem custo constante; aqui a janela é recortada em memória porque a tela
      // ainda pensa em índices.
      const todos = await listarLeads(to + 1, campaignId);
      const janela = todos.slice(from, to + 1);
      this.leads = janela as any;
      window.dispatchEvent(new CustomEvent('leads-persisted'));
      return janela as any;
    } catch (e) {
      console.error('LeadService: Failed to fetch range', e);
      return [];
    }
  }

  /**
   * Carrega os detalhes pesados (fotos, geometry) para um lead específico
   */
  async fetchLeadDetails(placeId: string): Promise<Partial<Lead>> {
    try {
      // `photos` agora são CHAVES do object storage, não base64 dentro da
      // linha. Este método deixou de ser "carregar os campos pesados": não há
      // mais campo pesado nesta tabela.
      const encontrados = await listarLeads(1);
      const lead = encontrados.find((l) => l.place_id === placeId);
      if (!lead) return {};

      const index = this.leads.findIndex((l) => l.place_id === placeId);
      if (index !== -1) {
        this.leads[index].photos = lead.photos as any;
        this.leads[index].geometry = lead.geometry as any;
        window.dispatchEvent(new CustomEvent('leads-persisted'));
      }
      return { photos: lead.photos as any, geometry: lead.geometry as any };
    } catch (e) {
      console.error('LeadService: Failed to fetch lead details', e);
      return {};
    }
  }

  private loadFromStorage() {
    // Deprecated: No longer loading from storage to avoid quota issues.
    // Migration logic handles moving it to Supabase.
    this.leads = [];
  }

  getLeads(): Lead[] {
    return this.leads;
  }

  /**
   * Verifica se o lead já existe globalmente para evitar duplicidade
   */
  exists(placeId: string): boolean {
    return this.leads.some(l => l.place_id === placeId);
  }

  async saveLeads(newLeads: Lead[]) {
    // Filtra apenas o que é REALMENTE novo
    const trulyNew = newLeads.filter(nl => !this.exists(nl.place_id));
    
    if (trulyNew.length > 0) {
      // PROCESSAMENTO PROFISSIONAL: Limpeza e Upload de Fotos
      const processedLeads = [];

      for (const lead of trulyNew) {
        const sanitizedLead = await this.prepareLeadForCloud(lead);
        processedLeads.push(sanitizedLead);
      }

      // 2.5: SEGURANÇA TOTAL: Garantir que campanhas existem antes de salvar leads
      const uniqueCampaignIds = [...new Set(processedLeads.map(l => l.campaignId).filter(Boolean))];
      if (uniqueCampaignIds.length > 0) {
        await syncToAll(async (client) => {
          for (const cId of uniqueCampaignIds) {
             const { data: exists } = await client.from('campaigns').select('id').eq('id', cId).maybeSingle();
             if (!exists) {
                console.warn(`LeadService: Campaign ${cId} missing in cloud. Creating placeholder.`);
                await client.from('campaigns').insert({
                  id: cId,
                  name: `Resgatada (${cId})`,
                  status: 'paused',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                });
             }
          }
        });
      }

      // 3. Salva no Supabase PRIMEIRO (Garantia de integridade) - Usando Lote (Batch) para evitar timeouts
      try {
        const batchSize = 25;
        for (let i = 0; i < processedLeads.length; i += batchSize) {
          const batch = processedLeads.slice(i, i + batchSize);
          
          await syncToAll(async (client) => {
            const { error } = await client.from('leads').upsert(batch.map(l => ({
              place_id: l.place_id,
              name: l.name,
              formatted_address: l.formatted_address,
              formatted_phone_number: l.formatted_phone_number,
              website: l.website,
              rating: l.rating,
              user_ratings_total: l.user_ratings_total,
              scanned_at: new Date(l.scannedAt || Date.now()).toISOString(),
              is_enriched: l.isEnriched || false,
              enriched_at: l.enriched_at ? new Date(l.enriched_at).toISOString() : null,
              whatsapp_status: l.whatsapp_status || 'pending',
              downloaded_at: l.downloaded_at ? new Date(l.downloaded_at).toISOString() : null,
              campaign_id: l.campaignId,
              types: l.types,
              geometry: l.geometry,
              photos: l.photos,
              whatsapp_data: l.whatsappData
            })));
            if (error) console.error(`LeadService: Batch ${i} save failed for one of the clients`, error);
          });
        }
      } catch (e) {
        console.error('LeadService: Critical failure in cloud saving loop', e);
      }

      // 4. Update local memory and notify UI
      this.leads = [...processedLeads, ...this.leads.filter(l => !trulyNew.some(nl => nl.place_id === l.place_id))];
      window.dispatchEvent(new CustomEvent('leads-persisted'));
    }
    
    return trulyNew.length;
  }

  /**
   * Prepara o lead para o cloud: remove lixo e sobe fotos base64 pro storage
   */
  private async prepareLeadForCloud(lead: Lead): Promise<Lead> {
    const sanitized = { ...lead };
    
    // 1. Limpa Gordura Geometria
    if (sanitized.geometry?.viewport) {
      delete sanitized.geometry.viewport;
    }

    // 2. Trata Fotos: Garante que NADA vai como Base64 pro banco (Storage Only)
    if (sanitized.photos && sanitized.photos.length > 0) {
      const uploadedPhotos = [];
      for (const photo of sanitized.photos) {
        let url = typeof photo === 'string' ? photo : (photo.url || '');
        
        if (url.startsWith('data:image')) {
          const publicUrl = await uploadLeadPhoto(sanitized.place_id, url);
          if (publicUrl) {
            uploadedPhotos.push({ url: publicUrl, uploaded: true });
          } else {
            // Se falhou o upload, MANTÉM a versão local para não sumir da UI
            // Mas marcamos como local_only para o sync do banco saber o que fazer
            uploadedPhotos.push(typeof photo === 'string' ? { url, local_only: true } : { ...photo, local_only: true });
          }
        } else if (url) {
          uploadedPhotos.push(typeof photo === 'string' ? { url } : photo);
        }
      }
      sanitized.photos = uploadedPhotos;
    }

    // 3. Trata foto do WhatsApp
    if (sanitized.whatsappData?.picture && sanitized.whatsappData.picture.startsWith('data:image')) {
       const publicUrl = await uploadLeadPhoto(sanitized.place_id + '_wa', sanitized.whatsappData.picture);
       if (publicUrl) {
          sanitized.whatsappData.picture = publicUrl;
       }
    }

    return sanitized;
  }

  private saveMinimalToStorage() {
    // Deprecated: No longer saving leads to localStorage.
    // Supabase is the single source of truth.
  }

  async updateLead(updatedLead: Lead) {
    const index = this.leads.findIndex(l => l.place_id === updatedLead.place_id);
    if (index !== -1) {
      // Processa fotos antes de atualizar (upload pro storage)
      const sanitized = await this.prepareLeadForCloud(updatedLead);
      
      this.leads[index] = { ...this.leads[index], ...sanitized };
      window.dispatchEvent(new CustomEvent('leads-persisted'));

    // Sync to Supabase
    try {
      // Para o banco de dados, removemos as fotos local_only (base64) para não estourar limite de tamanho da query
      const photosForDb = sanitized.photos?.map(p => {
        if ((p as any).url?.startsWith('data:') || (p as any).local_only) {
          return { url: 'pending_upload', error: true };
        }
        return p;
      });

      await syncToAll(async (client) => {
        const { error } = await client.from('leads').upsert({
          place_id: sanitized.place_id,
          name: sanitized.name,
          formatted_address: sanitized.formatted_address,
          formatted_phone_number: sanitized.formatted_phone_number,
          website: sanitized.website,
          rating: sanitized.rating,
          user_ratings_total: sanitized.user_ratings_total,
          scanned_at: new Date(sanitized.scannedAt || Date.now()).toISOString(),
          is_enriched: sanitized.isEnriched || false,
          enriched_at: sanitized.enriched_at ? new Date(sanitized.enriched_at).toISOString() : (sanitized.isEnriched ? new Date().toISOString() : null),
          whatsapp_status: sanitized.whatsapp_status || (sanitized.whatsappData?.verified ? (sanitized.whatsappData.exists ? 'exists' : 'not_exists') : 'pending'),
          downloaded_at: sanitized.downloaded_at ? new Date(sanitized.downloaded_at).toISOString() : null,
          campaign_id: sanitized.campaignId,
          types: sanitized.types,
          geometry: sanitized.geometry,
          photos: photosForDb,
          whatsapp_data: sanitized.whatsappData || (sanitized as any).whatsapp_data || null
        });
          
        if (error) {
          handleSupabaseError(error, OperationType.UPDATE, 'leads');
        }
      });
      } catch (e) {
        console.error('LeadService: Failed to update in cloud', e);
      }
    }
  }

  async deleteLead(placeId: string) {
    this.leads = this.leads.filter(l => l.place_id !== placeId);
    window.dispatchEvent(new CustomEvent('leads-persisted'));

    try {
      await syncToAll(async (client) => {
        await client.from('leads').delete().eq('place_id', placeId);
      });
    } catch (e) {
      console.error('LeadService: Failed to delete from cloud', e);
    }
  }

  async deleteMultipleLeads(placeIds: string[]) {
    this.leads = this.leads.filter(l => !placeIds.includes(l.place_id));
    window.dispatchEvent(new CustomEvent('leads-persisted'));

    try {
      await syncToAll(async (client) => {
        await client.from('leads').delete().in('place_id', placeIds);
      });
    } catch (e) {
      console.error('LeadService: Failed to delete multiple from cloud', e);
    }
  }

  /**
   * Sincroniza com a API externa de clientes da Nyroh.
   * Filtra leads locais que NÃO estão enriquecidos e que coincidem com os clientes da API externa (por telefone limpo ou por nome limpo),
   * e os exclui do banco de dados (Supabase) pra limpar campanhas.
   * @param specificLeads Opcional. Lista específica de leads para avaliar (para higienizar bloco a bloco). Se omitido, avalia os últimos 1000 leads gerais do DB.
   * @returns Resumo da sincronização contendo quantidade encontrada e excluída.
   */
  async syncWithNyrohClients(specificLeads?: any[]): Promise<{ checked: number, matches: number, deletedCount: number, error?: string }> {
    try {
      // 1. Buscar os clientes já cadastrados.
      //
      // O QUE SAIU DAQUI: um `fetch` para `https://www.nyroh.com.br/api/...` com
      // `Authorization: Bearer nyroh_live_<...>` — uma chave FIXA no código, que
      // ia inteira para o bundle entregue ao navegador e não podia ser revogada
      // sem deploy. (O valor não é reproduzido aqui de propósito: ele continua
      // válido no servidor antigo até ser rotacionado.)
      //
      // Agora é a mesma API do resto do app, com a sessão do operador. Não há
      // token no bundle porque não há token: a credencial é o cookie httpOnly.
      const pagina = await listarClientesCadastrados();
      const apiClients = pagina.map((c) => ({
        id: c.id,
        name: c.nome,
        phone: c.telefone ?? '',
        niche: c.categoria ?? undefined,
        status: c.status,
      }));

      // Função de apoio para limpar números
      const cleanPhone = (num: any) => {
        if (!num) return '';
        return num.toString().replace(/\D/g, '');
      };

      // Criar mapas dos dados da API para cruzamento super rápido
      const apiPhoneMap = new Map();
      const apiNameMap = new Map();

      apiClients.forEach(c => {
        const p = cleanPhone(c.phone);
        if (p) apiPhoneMap.set(p, c);
        
        if (c.name) {
          const cleanName = c.name.toLowerCase().trim().replace(/\s+/g, '');
          apiNameMap.set(cleanName, c);
        }
      });

      let nonEnrichedLeads: any[] = [];

      if (specificLeads && Array.isArray(specificLeads)) {
        // No frontend, a propriedade pode ser camelCase: isEnriched ou snake_case: is_enriched
        nonEnrichedLeads = specificLeads.filter(l => !(l.isEnriched || l.is_enriched));
      } else {
        // 2. Buscar os últimos 1000 leads mais recentes do banco de dados para garantir máxima velocidade e precisão nos leads mais novos de todas as campanhas
        // Mil leads por cursor, não `limit(1000)` sobre a tabela inteira.
        const leads = await listarLeads(1000);
        nonEnrichedLeads = leads.filter((l: any) => !l.is_enriched && !l.isEnriched) as any;
      }
      
      // 3. Fazer cruzamento cruzado inteligente
      const placeIdsToDelete = new Set<string>();

      nonEnrichedLeads.forEach(lead => {
        const phone = lead.formatted_phone_number || lead.formattedPhoneNumber;
        const localCleanPhone = cleanPhone(phone);
        const name = lead.name;
        const placeId = lead.place_id || lead.placeId;
        
        let isMatch = false;
        // Match principal: Por número de telefone limpo
        if (localCleanPhone && apiPhoneMap.has(localCleanPhone)) {
          isMatch = true;
        } else if (name) {
          // Fallback de contingência: Por nome limpo (sem espaços, minúsculo)
          const localCleanName = name.toLowerCase().trim().replace(/\s+/g, '');
          if (apiNameMap.has(localCleanName)) {
            isMatch = true;
          }
        }

        if (isMatch && placeId) {
          placeIdsToDelete.add(placeId);
        }
      });

      const deletedCount = placeIdsToDelete.size;

      // 4. Se houver redundâncias/encontros, fazer a exclusão do banco
      if (deletedCount > 0) {
        const idsArray = Array.from(placeIdsToDelete);
        
        // Excluir nas conexões centralizadas com Supabase
        await syncToAll(async (client) => {
          const batchSize = 100;
          for (let i = 0; i < idsArray.length; i += batchSize) {
            const batch = idsArray.slice(i, i + batchSize);
            await client.from('leads').delete().in('place_id', batch);
          }
        });

        // Atualizar lista em memória e re-renderizar a UI do Dashboard de leads
        // `placeId` em camelCase nunca existiu no tipo `Lead` — era uma defesa
        // contra um formato que só aparecia quando o dado vinha cru do
        // PostgREST. Com o contrato, o formato é um só; o fallback fica por
        // segurança para dado antigo em localStorage, mas com o cast explícito.
        this.leads = this.leads.filter(
          l => !placeIdsToDelete.has(l.place_id || (l as unknown as { placeId?: string }).placeId || ''),
        );
        window.dispatchEvent(new CustomEvent('leads-persisted'));
      }

      return {
        checked: nonEnrichedLeads.length,
        matches: deletedCount,
        deletedCount
      };

    } catch (e: any) {
      console.error('LeadService: Sync error', e);
      return {
        checked: 0,
        matches: 0,
        deletedCount: 0,
        error: e?.message || String(e)
      };
    }
  }

  clearAll() {
    this.leads = [];
    localStorage.removeItem('google_maps_leads_master');
    window.dispatchEvent(new CustomEvent('leads-persisted'));
  }
}

export const leadService = new LeadService();
